import React, { useEffect, useRef, useState } from "react";
import { Button } from "@heroui/react";
import { LandingTopBar } from "./LandingTopBar";
import { LandingDock } from "./LandingDock";
import { ScrollSection } from "./ScrollSection";
import { LandingPlatformStackAsset } from "./LandingPlatformStackAsset";
import { LandingFooter } from "./LandingFooter";
import { LandingDeadline } from "./LandingDeadline";
import { LandingGuaranteePreview } from "./LandingGuaranteePreview";
import { SHOPIFY_APP_STORE_URL } from "./landingNav";
import { DxlLogo } from "./DxlLogo";

const serviceCards = [
  {
    titleTop: "Withdrawals",
    titleBottom: "Intake",
    tags: ["Portal embed", "Case inbox", "Audit trail"],
    copy: "Configure the embed in the portal, paste the token script once, manage cases from one inbox."
  },
  {
    titleTop: "Guarantees",
    titleBottom: "& GARAN",
    tags: ["Legal notice", "GARAN labels", "Product keys"],
    copy: "Enable the module, add product keys and GARAN data, then place the guarantees embed on PDPs."
  },
  {
    titleTop: "AI",
    titleBottom: "Labels",
    tags: ["Art. 50", "Storefront overlay", "Portal config"],
    copy: "Mark AI media in the portal, set appearance, and load the AI labels embed on product pages."
  }
];

const flowSteps = [
  { title: "Create account", body: "Sign up for the standalone portal." },
  { title: "Link & enable", body: "Copy your embed token. Enable Guarantees and AI Labels in billing." },
  { title: "Configure", body: "Add products and appearance — then place the scripts on your shop." }
];

export function LandingPage({ authenticated = false }) {
  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [dockVisible, setDockVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => {
      const y = el.scrollTop;
      const hideHeader = y > 72;
      const footerTop = footerRef.current?.offsetTop ?? Number.POSITIVE_INFINITY;
      const footerReached = y + el.clientHeight >= footerTop - 40;
      setHeaderHidden(hideHeader);
      setDockVisible(hideHeader && !footerReached);
    };

    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goSignup = () => {
    window.location.href = authenticated ? "/portal/onboarding" : "/users/sign_up";
  };

  return (
    <div className="landing-root">
      <LandingTopBar hidden={headerHidden} variant="marketing" showSettingsLink={authenticated} />

      <div ref={scrollRef} className="landing-scroll">
        <ScrollSection id="home" className="landing-hero landing-hero--dxl">
          <div className="landing-hero__center">
            <h1 className="landing-hero__wordmark">
              <span className="landing-hero__wordmark-row">
                <span className="landing-hero__wordmark-eu">EU</span>
                <LandingPlatformStackAsset />
              </span>
              <span className="landing-hero__wordmark-line">Compliance Suite</span>
            </h1>

            <div className="landing-hero__headline">
              <p className="landing-hero__byline">
                <span className="landing-hero__by">by</span>
                <DxlLogo className="landing-hero__brand-logo" />
              </p>
              <p className="landing-hero__subtitle">
                Withdrawals, legal guarantee + GARAN, and AI labels — configured in one portal,
                embedded on any storefront.
              </p>
              <LandingDeadline />
              <div className="landing-hero__cta-row">
                <Button variant="primary" size="lg" className="portal-accent-button" onPress={goSignup}>
                  {authenticated ? "Open portal" : "Create account"}
                </Button>
                <a href="#preview" className="landing-hero__secondary-link">
                  Storefront preview
                </a>
              </div>
            </div>
          </div>
        </ScrollSection>

        <ScrollSection id="paths" className="landing-section-block landing-section-block--tight">
          <div className="landing-paths landing-paths--ranked">
            <article className="landing-path-card landing-path-card--primary">
              <p className="landing-path-card__kicker">Primary</p>
              <h3>Portal account</h3>
              <p>
                Create an account, enable modules, add products, and place your embed scripts —
                for any shop system.
              </p>
              <Button variant="primary" className="portal-accent-button" onPress={goSignup}>
                {authenticated ? "Open portal" : "Create account"}
              </Button>
            </article>
            <article className="landing-path-card landing-path-card--secondary">
              <p className="landing-path-card__kicker">Shopify</p>
              <h3>App Store install</h3>
              <p>
                Prefer native Shopify admin? Install the suite, then manage the same modules in
                your store.
              </p>
              <a
                href={SHOPIFY_APP_STORE_URL}
                className="landing-path-card__btn-outline"
                target="_blank"
                rel="noreferrer"
              >
                Open App Store
              </a>
            </article>
          </div>
        </ScrollSection>

        <ScrollSection
          id="preview"
          className="landing-section-block landing-section-block--alt landing-section-block--tight"
        >
          <LandingGuaranteePreview />
        </ScrollSection>

        <ScrollSection id="features" className="landing-features">
          <header className="landing-services__intro">
            <p className="landing-services__eyebrow">Modules</p>
            <h2 className="landing-section-title landing-services__title">
              Configure once. Embed everywhere.
            </h2>
          </header>

          <div className="landing-services__stack">
            {serviceCards.map((card, index) => (
              <article
                key={card.titleBottom}
                className="landing-service-card"
                style={{ "--service-index": index }}
              >
                <h3 className="landing-service-card__title">
                  <span>{card.titleTop}</span>
                  <span>{card.titleBottom}</span>
                </h3>
                <div className="landing-service-card__tags">
                  {card.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
                <p className="landing-service-card__copy">{card.copy}</p>
              </article>
            ))}
          </div>
        </ScrollSection>

        <ScrollSection id="flow" className="landing-section-block landing-section-block--tight">
          <header className="landing-paths__intro">
            <p className="landing-services__eyebrow">Portal flow</p>
            <h2 className="landing-section-title">From account to live storefront</h2>
          </header>
          <ol className="landing-process">
            {flowSteps.map((step, i) => (
              <li key={step.title} className="landing-process__step">
                <span className="landing-process__num">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </ScrollSection>

        <ScrollSection id="cta" className="landing-cta landing-cta--tight">
          <h2 className="landing-cta__title">
            Ready for <span className="landing-hero__accent">27 September</span>
          </h2>
          <p className="landing-cta__subtitle">
            Create your account, enable modules, and configure embeds in the portal.
          </p>
          <Button variant="primary" size="lg" className="portal-accent-button min-w-48" onPress={goSignup}>
            {authenticated ? "Open portal" : "Create account"}
          </Button>
        </ScrollSection>

        <div ref={footerRef}>
          <LandingFooter scrollContainerRef={scrollRef} />
        </div>
      </div>

      <LandingDock visible={dockVisible} />
    </div>
  );
}
